import { api } from '@/lib/api/requests'
import type {
  CanEditResponse,
  CreatePlayerPayload,
  CreatePlayerResponse,
  OrgPlayersResponse,
  PlayerBootstrapResponse,
  PlayerPartnersResponse,
  PlayerTournamentsResponse,
  PublicLeaderboardResponse,
  PublicPlayerResponse,
  RatingTimelineResponse,
  RecentMatchesResponse,
  SendPlayerInviteResponse,
} from '@/features/players/types'
import type { PlayerRankingResponse, RankingBoardResponse } from './rankingPoints'

// `limit` (optional) caps the rows the backend returns at read time — the home
// needs only a handful, not the full board. `total` still reports the full
// count. Omit it (e.g. the /players full leaderboard) to get every row.
export async function fetchPublicLeaderboard(
  contextId: string,
  limit?: number,
): Promise<PublicLeaderboardResponse> {
  const limitParam =
    limit != null && Number.isFinite(limit) && limit > 0 ? `&limit=${Math.floor(limit)}` : ''
  return api.get<PublicLeaderboardResponse>(
    `/leaderboards/global?context_id=${encodeURIComponent(contextId)}${limitParam}`,
    { auth: 'omit', handleAuthErrors: false },
  )
}

// "My clubs" scope — the union of every club the viewer follows, ranked within
// that union. Authed: the backend resolves the followed-org set from the token
// (never trusts a client list). 0 followed clubs → 200 with an empty list,
// which drives the "follow a club" empty state (backend 94b68fb7).
export async function fetchMyClubsLeaderboard(contextId: string): Promise<PublicLeaderboardResponse> {
  return api.get<PublicLeaderboardResponse>(
    `/leaderboards/my-clubs?context_id=${encodeURIComponent(contextId)}`,
    { handleAuthErrors: false },
  )
}

// "Specific club" scope — one followed club, ranked within that club (rank =
// org_rank). Authed; gated by requirePrivateOrgReadAccess (registered viewer +
// follower), which is satisfied because the picked club is one the viewer follows.
export async function fetchOrgLeaderboard(
  orgId: string,
  contextId: string,
): Promise<PublicLeaderboardResponse> {
  return api.get<PublicLeaderboardResponse>(
    `/organizations/${encodeURIComponent(orgId)}/leaderboards?context_id=${encodeURIComponent(contextId)}`,
    { handleAuthErrors: false },
  )
}

export async function fetchOrgPlayers(orgId: string): Promise<OrgPlayersResponse> {
  return api.get<OrgPlayersResponse>(`/organizations/${encodeURIComponent(orgId)}/players`, {
    handleAuthErrors: false,
  })
}

// `recentLimit` (optional) caps the bootstrap's recent_matches at read time
// (backend f38fa8f). The /profile page passes 5 so the one bootstrap call is the
// single source of its recent matches (replacing the separate profile-secondary
// fetch); the public player card omits it and keeps the backend default.
export async function fetchProfileBootstrap(
  playerId: string,
  orgId?: string,
  recentLimit?: number,
): Promise<PlayerBootstrapResponse> {
  const params = new URLSearchParams()
  if (orgId) params.set('orgId', orgId)
  if (recentLimit != null && Number.isFinite(recentLimit) && recentLimit > 0) {
    params.set('recent_limit', String(Math.floor(recentLimit)))
  }
  const query = params.toString()
  const path = query
    ? `/players/${encodeURIComponent(playerId)}/profile-bootstrap?${query}`
    : `/players/${encodeURIComponent(playerId)}/profile-bootstrap`
  return api.get<PlayerBootstrapResponse>(path, { handleAuthErrors: false })
}

export async function fetchPlayerCanEdit(orgId: string, playerId: string): Promise<CanEditResponse> {
  return api.get<CanEditResponse>(
    `/organizations/${encodeURIComponent(orgId)}/players/${encodeURIComponent(playerId)}/can-edit`,
    { handleAuthErrors: false },
  )
}

export async function fetchPublicPlayer(playerId: string): Promise<PublicPlayerResponse> {
  return api.get<PublicPlayerResponse>(`/players/public/${encodeURIComponent(playerId)}`, {
    auth: 'omit',
    handleAuthErrors: false,
  })
}

export async function fetchPublicRecentMatches(
  playerId: string,
  limit: number,
  offset: number,
): Promise<RecentMatchesResponse> {
  return api.get<RecentMatchesResponse>(
    `/players/public/${encodeURIComponent(playerId)}/recent-matches?limit=${limit}&offset=${offset}`,
    {
      auth: 'omit',
      handleAuthErrors: false,
    },
  )
}

export async function fetchPlayerRecentMatches(
  playerId: string,
  options: { limit: number; offset: number; orgId?: string; view?: string },
): Promise<RecentMatchesResponse> {
  const query = new URLSearchParams()
  query.set('limit', String(options.limit))
  query.set('offset', String(options.offset))
  if (options.orgId) query.set('orgId', options.orgId)
  if (options.view) query.set('view', options.view)
  return api.get<RecentMatchesResponse>(
    `/players/${encodeURIComponent(playerId)}/recent-matches?${query.toString()}`,
    {
      handleAuthErrors: false,
    },
  )
}

// Player tournaments + placement aggregates (BE-1). Authed read by playerId.
export async function fetchPlayerTournaments(playerId: string): Promise<PlayerTournamentsResponse> {
  return api.get<PlayerTournamentsResponse>(
    `/players/${encodeURIComponent(playerId)}/tournaments`,
    { handleAuthErrors: false },
  )
}

// Public variant by shortId (anonymous; D1.2-gated server-side).
export async function fetchPublicPlayerTournaments(shortId: string): Promise<PlayerTournamentsResponse> {
  return api.get<PlayerTournamentsResponse>(
    `/players/public/${encodeURIComponent(shortId)}/tournaments`,
    { handleAuthErrors: false },
  )
}

// Rating-over-time series for one sport context (BE-3). `context` is required.
export async function fetchPlayerRatingTimeline(
  playerId: string,
  context: string,
): Promise<RatingTimelineResponse> {
  return api.get<RatingTimelineResponse>(
    `/players/${encodeURIComponent(playerId)}/rating-timeline?context=${encodeURIComponent(context)}`,
    { handleAuthErrors: false },
  )
}

// Public variant by shortId (anonymous; D1.2-gated server-side → empty series).
export async function fetchPublicPlayerRatingTimeline(
  shortId: string,
  context: string,
): Promise<RatingTimelineResponse> {
  return api.get<RatingTimelineResponse>(
    `/players/public/${encodeURIComponent(shortId)}/rating-timeline?context=${encodeURIComponent(context)}`,
    { handleAuthErrors: false },
  )
}

// Partners & Rivals (H2H) aggregation (BE-2). Authed read by playerId.
export async function fetchPlayerPartners(playerId: string): Promise<PlayerPartnersResponse> {
  return api.get<PlayerPartnersResponse>(
    `/players/${encodeURIComponent(playerId)}/partners`,
    { handleAuthErrors: false },
  )
}

// Public variant by shortId (anonymous; D1.2-gated server-side → empty shell).
export async function fetchPublicPlayerPartners(shortId: string): Promise<PlayerPartnersResponse> {
  return api.get<PlayerPartnersResponse>(
    `/players/public/${encodeURIComponent(shortId)}/partners`,
    { handleAuthErrors: false },
  )
}

export async function createPlayer(
  orgId: string,
  payload: CreatePlayerPayload,
): Promise<CreatePlayerResponse> {
  return api.post<CreatePlayerResponse>(`/organizations/${encodeURIComponent(orgId)}/players`, {
    body: payload,
    handleAuthErrors: false,
  })
}

export async function updatePlayer(
  playerId: string,
  payload: Record<string, unknown>,
): Promise<Record<string, unknown>> {
  return api.patch<Record<string, unknown>>(`/players/${encodeURIComponent(playerId)}`, {
    body: payload,
    handleAuthErrors: false,
  })
}

export async function sendPlayerInvite(
  orgId: string,
  playerId: string,
): Promise<SendPlayerInviteResponse> {
  // Pre-launch manual invite (backend playerInviteSend.mjs). Always
  // owner-initiated — never called from a create-player success path.
  return api.post<SendPlayerInviteResponse>(
    `/organizations/${encodeURIComponent(orgId)}/players/${encodeURIComponent(playerId)}/auth/send-invite`,
    { handleAuthErrors: false },
  )
}

export async function deletePlayer(
  playerId: string,
  orgId: string,
): Promise<Record<string, unknown>> {
  const query = new URLSearchParams({ orgId })
  return api.del<Record<string, unknown>>(`/players/${encodeURIComponent(playerId)}?${query.toString()}`, {
    handleAuthErrors: false,
  })
}

// Ranking points (docs/adr-tournament-ranking-points.md §4) — the profile card's
// one read per sport context. Authed by playerId (a registered viewer; `orgId`
// narrows to one club's tournaments and needs its follower)…
export async function fetchPlayerRanking(
  playerId: string,
  contextId: string,
  orgId?: string,
): Promise<PlayerRankingResponse> {
  const org = orgId ? `&orgId=${encodeURIComponent(orgId)}` : ''
  return api.get<PlayerRankingResponse>(
    `/players/${encodeURIComponent(playerId)}/ranking?contextId=${encodeURIComponent(contextId)}${org}`,
    { handleAuthErrors: false },
  )
}

// …and by shortId for anyone (D1.2: an anonymous caller gets livePoints null, no awards).
export async function fetchPublicPlayerRanking(shortId: string, contextId: string): Promise<PlayerRankingResponse> {
  return api.get<PlayerRankingResponse>(
    `/players/public/${encodeURIComponent(shortId)}/ranking?contextId=${encodeURIComponent(contextId)}`,
    { handleAuthErrors: false },
  )
}

// The Ranking board (§7, D14.12). Global (no orgId) is public; one club's board
// needs a registered follower of that club. gender / age=junior filter server
// side and keep the board's ranks; the page filters its cached rows instead.
// `limit` slices the ranked rows server side (the club Statistics' top 5).
export async function fetchRankingLeaderboard(
  contextId: string,
  orgId?: string,
  limit?: number,
): Promise<RankingBoardResponse> {
  const org = orgId ? `&orgId=${encodeURIComponent(orgId)}` : ''
  const cap = limit && limit > 0 ? `&limit=${Math.floor(limit)}` : ''
  return api.get<RankingBoardResponse>(
    `/leaderboards/ranking?contextId=${encodeURIComponent(contextId)}${org}${cap}`,
    orgId ? { handleAuthErrors: false } : { auth: 'omit', handleAuthErrors: false },
  )
}
